import React, { useEffect, useMemo, useState } from 'react'
import { Flex, Flexed, Scroll, currency } from '../../utilities/Shared'
import { sortit, sortnum } from '../../utilities/Sort'
import axios from 'axios'
import { NavLink } from 'react-router-dom'
import DanceComponent from '../../utilities/DanceComponent'

/* ---------------- DATE HELPERS ---------------- */

const startOfDay = d =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate())

const endOfDay = d =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59)

const getWeekRange = (offsetWeeks = 0) => {
  const base = new Date()
  const day = base.getDay() || 7 // Monday = 1
  base.setDate(base.getDate() - day + 1 + offsetWeeks * 7)

  const start = startOfDay(base)
  const end = endOfDay(new Date(start.getTime() + 6 * 86400000))

  return { start, end }
}

const getMonthRange = () => {
  const d = new Date()
  return {
    start: new Date(d.getFullYear(), d.getMonth(), 1),
    end: new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59)
  }
}

const getQuarterRange = () => {
  const d = new Date()
  const q = Math.floor(d.getMonth() / 3)
  return {
    start: new Date(d.getFullYear(), q * 3, 1),
    end: new Date(d.getFullYear(), q * 3 + 3, 0, 23, 59, 59)
  }
}

const getYearRange = () => {
  const y = new Date().getFullYear()
  return {
    start: new Date(y, 0, 1),
    end: new Date(y, 11, 31, 23, 59, 59)
  }
}

/* ---------------- PRICE FROM BILLABLE ---------------- */

const jobTotal = job =>
  Array.isArray(job.billable)
    ? job.billable.reduce((t, b) => t + Number(b.cost || 0), 0)
    : 0

const sumJobs = (jobs, predicate) =>
  jobs.filter(predicate).reduce((t, j) => t + jobTotal(j), 0)

/* ---------------- COMPONENT ---------------- */

const Salesdetails = ({ salesreps = [], jobstoday = [] }) => {
  const [jobs, setJobs] = useState([])
  const [order, setOrder] = useState('ASC')
  const [cfpsales, setCFPsales] = useState({})
  const [repTotals, setRepTotals] = useState([])

  /* ---------------- DATE RANGES ---------------- */

  const ranges = useMemo(() => ({
    today: {
      start: startOfDay(new Date()),
      end: endOfDay(new Date())
    },
    thisWeek: getWeekRange(0),
    lastWeek: getWeekRange(-1),
    month: getMonthRange(),
    quarter: getQuarterRange(),
    year: getYearRange()
  }), [])



  /* ---------------- DATA FETCH ---------------- */

  useEffect(() => {
    if (!salesreps.length) return

    const getJobs = async () => {
      try {
        const repIds = salesreps.map(r => r.empId)
        const res = await axios.post(
          'https://highgroundapi.herokuapp.com/jobs/getsalesjobs',
           //'http://localhost:5001/jobs/getsalesjobs',
          { repIds }
        )
        setJobs(res.data || [])
      } catch (err) {
        console.error(err)
      }
    }

    const getCFPSales = async () => {
      try {
        const res = await axios.get(
          'https://cfpapi-6ab062006fa0.herokuapp.com/jobs/get/all/sales'
        )
        setCFPsales(res.data || {})
      } catch (err) {
        console.error(err)
      }
    }

    getJobs()
    getCFPSales()
  }, [salesreps])

  /* ---------------- REP TOTALS ---------------- */

  const repTotalsBase = useMemo(() => {
    return salesreps.map(rep => ({
      id: rep.empId,
      name: `${rep.firstName} ${rep.lastName?.[0] || ''}`,

      today: sumJobs(jobs, j => {
        const d = new Date(j.createdAt)
        return j.salesrep === rep.empId && d >= ranges.today.start && d <= ranges.today.end
      }),

      thisweek: sumJobs(jobs, j => {
        const d = new Date(j.createdAt)
        return j.salesrep === rep.empId && d >= ranges.thisWeek.start && d <= ranges.thisWeek.end
      }),

      lastweek: sumJobs(jobs, j => {
        const d = new Date(j.createdAt)
        return j.salesrep === rep.empId && d >= ranges.lastWeek.start && d <= ranges.lastWeek.end
      }),

      MTD: sumJobs(jobs, j => {
        const d = new Date(j.createdAt)
        return j.salesrep === rep.empId && d >= ranges.month.start && d <= ranges.month.end
      }),

      QTD: sumJobs(jobs, j => {
        const d = new Date(j.createdAt)
        return j.salesrep === rep.empId && d >= ranges.quarter.start && d <= ranges.quarter.end
      }),

      YTD: sumJobs(jobs, j => {
        const d = new Date(j.createdAt)
        return j.salesrep === rep.empId && d >= ranges.year.start && d <= ranges.year.end
      })
    }))
  }, [jobs, salesreps, ranges])

  useEffect(() => {
    setRepTotals(repTotalsBase)
  }, [repTotalsBase])

  /* ---------------- GRAND TOTALS ---------------- */
console.log(repTotals)
  const totals = useMemo(() => ({
    today:
      sumJobs(jobstoday, () => true) +
      Number(cfpsales?.today || 0),

    week:
      sumJobs(jobs, j => {
        const d = new Date(j.createdAt)
        return d >= ranges.thisWeek.start && d <= ranges.thisWeek.end
      }) + Number(cfpsales?.week || 0),

    lastweek:
      sumJobs(jobs, j => {
        const d = new Date(j.createdAt)
        return d >= ranges.lastWeek.start && d <= ranges.lastWeek.end
      }) + Number(cfpsales?.lastweek || 0),

    month:
      sumJobs(jobs, j => {
        const d = new Date(j.createdAt)
        return d >= ranges.month.start && d <= ranges.month.end
      }) + Number(cfpsales?.month || 0),

    quarter:
      sumJobs(jobs, j => {
        const d = new Date(j.createdAt)
        return d >= ranges.quarter.start && d <= ranges.quarter.end
      }) + Number(cfpsales?.quarter || 0),

    year:
      sumJobs(jobs, j => {
        const d = new Date(j.createdAt)
        return d >= ranges.year.start && d <= ranges.year.end
      }) + Number(cfpsales?.year || 0)
  }), [jobs, jobstoday, cfpsales, ranges])

  /* ---------------- RENDER ---------------- */

  return (
    <>
      <Flexed margin="30px 0 0" bgColor="#8398AB" color="white" padding="5px 0">
        <Flex fontSize="calc(1.1rem + .4vw)">Sales Report</Flex>
      </Flexed>

      {/* HEADERS */}
      <Flexed className="hand" style={{ background: 'white', borderBottom: '1px solid black' }}>
        <Flex flex="1.5" onClick={() => sortit('name', order, repTotals, setRepTotals, setOrder)}>Sales Rep</Flex>
        <Flex onClick={() => sortnum('today', order, repTotals, setRepTotals, setOrder)}>Today</Flex>
        <Flex onClick={() => sortnum('thisweek', order, repTotals, setRepTotals, setOrder)}>This Week</Flex>
        <Flex onClick={() => sortnum('lastweek', order, repTotals, setRepTotals, setOrder)}>Last Week</Flex>
        <Flex onClick={() => sortnum('MTD', order, repTotals, setRepTotals, setOrder)}>MTD</Flex>
        <Flex onClick={() => sortnum('QTD', order, repTotals, setRepTotals, setOrder)}>QTD</Flex>
        <Flex onClick={() => sortnum('YTD', order, repTotals, setRepTotals, setOrder)}>YTD</Flex>
      </Flexed>

      {/* ROWS */}
      <Scroll border="1px solid #303030">
        {repTotals.map(rep => (
          <Flexed key={rep.id} className="normalrow hand" padding="3px">
            <Flex flex="1.5">
              {/* <NavLink to={`https://highgroundsoftware.com/dash/dashboard/?des=10301&r=${rep.id}&s=today`}>
                {rep.name}
              </NavLink> */}
              <NavLink to={`salesreport?r=${rep.id}`}>
  {rep.name}
</NavLink>

            </Flex>
            <Flex>${currency(rep.today)}</Flex>
            <Flex>${currency(rep.thisweek)}</Flex>
            <Flex>${currency(rep.lastweek)}</Flex>
            <Flex>${currency(rep.MTD)}</Flex>
            <Flex>${currency(rep.QTD)}</Flex>
            <Flex>${currency(rep.YTD)}</Flex>
          </Flexed>
        ))}
        {/* CFP TOTALS */}
<Flexed bgColor="#1E5D78" color="white" padding="5px 0">
  <Flex flex="1.5">CFP Sales</Flex>
  <Flex>${currency(cfpsales?.today || 0)}</Flex>
  <Flex>${currency(cfpsales?.week || 0)}</Flex>
  <Flex>${currency(cfpsales?.lastweek || 0)}</Flex>
  <Flex>${currency(cfpsales?.month || 0)}</Flex>
  <Flex>${currency(cfpsales?.quarter || 0)}</Flex>
  <Flex>${currency(cfpsales?.year || 0)}</Flex>
</Flexed>
      </Scroll>

      {/* TOTALS */}
      <Flexed bgColor="#0E425A" color="white" padding="5px 0">
        <Flex flex="1.5" />
        <Flex>${currency(totals.today)}</Flex>
        <Flex>${currency(totals.week)}</Flex>
        <Flex>${currency(totals.lastweek)}</Flex>
        <Flex>${currency(totals.month)}</Flex>
        <Flex>${currency(totals.quarter)}</Flex>
        <Flex>${currency(totals.year)}</Flex>
      </Flexed>



      {/* {totals.week > 150000 && (
        <Flexed justifyContent="center">
          <DanceComponent />
        </Flexed>
      )} */}
    </>
  )
}

export default Salesdetails
